import { Alert, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { Calendar } from '@ophelos/ui/components/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ophelos/ui/components/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@ophelos/ui/components/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ophelos/ui/components/tooltip';
import { BadgePercent, Calendar as CalendarIcon, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Trans } from 'react-i18next';
import { BottomActionBar } from '@/components/layout/BottomActionBar';
import { AffordabilityCommitment } from '@/components/payments/AffordabilityCommitment';
import { MultibancoReference } from '@/components/payments/MultibancoReference';
import { PayerEmailField } from '@/components/payments/PayerEmailField';
import { Countdown } from '@/components/ui/Countdown';
import { CurrencyDisplay } from '@/components/ui/CurrencyDisplay';
import { useDebtData } from '@/contexts/DebtDataContext';
import { useTranslation } from '@/hooks/useTranslation';
import { applyCredit } from '@/lib/accountNet';
import { discountedAmountMillicents } from '@/lib/discount-offers';
import { type MultibancoReference as MultibancoReferenceType } from '@/lib/payment-api';

interface PayInFullTabProps {
  caseId: string;
  amount: number;
  /** Credit attributed to this debt, taken off after any discount. */
  creditApplied?: number;
  currency?: string;
  onContinue?: () => void;
  allowFullPayLater?: boolean; // If false, locks date to "today"
  maxDaysInFuture?: number;
  onSelectPaymentMethod?: (amount: number, type: 'full' | 'partial', date?: Date) => void;
  needsEmail?: boolean;
  payerEmail?: string;
  onPayerEmailChange?: (value: string) => void;
  emailValid?: boolean;
}

export function PayInFullTab({
  caseId,
  amount,
  creditApplied = 0,
  currency = 'EUR',
  onContinue: _onContinue,
  allowFullPayLater = true,
  onSelectPaymentMethod,
  maxDaysInFuture = 14,
  needsEmail = false,
  payerEmail = '',
  onPayerEmailChange,
  emailValid = true,
}: PayInFullTabProps) {
  const { t } = useTranslation();
  const { activeOffers } = useDebtData();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  const [open, setOpen] = useState(false);
  const [multibancoRef, setMultibancoRef] = useState<MultibancoReferenceType | null>(null);

  const offer = activeOffers[caseId];
  const currentAmountMillicents = Math.round(amount * 100_000);
  const discountedMillicents = offer ? discountedAmountMillicents(offer, 'pay_now', currentAmountMillicents) : null;
  const hasEligibleDiscount = offer != null && discountedMillicents != null && offer.status !== 'expired' && offer.status !== 'superseded';
  const showDiscount = hasEligibleDiscount;
  const discountedAmount = showDiscount ? (discountedMillicents as number) / 100_000 : amount;
  // The credit attributed to this debt comes off after the discount, matching the account
  // surfaces and the figure the dashboard card showed before the customer got here.
  const displayAmount = applyCredit(discountedAmount, creditApplied);
  const payNowPct = offer?.configSnapshot.options?.pay_now?.percentage ?? 0;
  const savingsAmount = showDiscount ? amount - discountedAmount : 0;

  const today = new Date();
  const maxDate = new Date();
  maxDate.setDate(today.getDate() + maxDaysInFuture);

  const isToday =
    selectedDate &&
    selectedDate.getDate() === today.getDate() &&
    selectedDate.getMonth() === today.getMonth() &&
    selectedDate.getFullYear() === today.getFullYear();

  const handleContinueToMethod = () => {
    // The full-balance pay applies the active discount when one is live.
    if (onSelectPaymentMethod) {
      onSelectPaymentMethod(displayAmount, 'full', isToday ? undefined : selectedDate);
    }
  };

  // Amount Selection Step
  return (
    <div className="w-full flex-1 min-h-0 stack items-center px-2 xs:px-4">
      <div className="flex-1 stack space-y-4">
        {/* Discount banner — sits above the amount so the customer sees it before the price */}
        {showDiscount && (
          <Alert variant="warning" className="w-full mx-auto">
            <BadgePercent />
            <AlertTitle>{t('discount.percentApplied', { pct: Math.round(payNowPct * 100) })}</AlertTitle>
            <AlertDescription className="flex">
              <Trans
                i18nKey="discount.youSavePayInFull"
                components={{
                  amount: <CurrencyDisplay amount={savingsAmount} currency={currency} className="font-semibold inline-flex gap-0" />,
                }}
              />
            </AlertDescription>
          </Alert>
        )}
        {/* Amount Display */}
        <div className="flex flex-col items-center">
          <div className="relative inline-block mt-18">
            {showDiscount && (
              <div className="flex flex-col items-center gap-1 mb-2">
                <CurrencyDisplay amount={amount} currency={currency} className="text-base line-through text-destructive" />
                <Countdown to={offer?.expiresAt} />
              </div>
            )}
            <div className="text-5xl font-bold mb-2">
              <CurrencyDisplay amount={displayAmount} currency={currency} className="text-5xl" centsClassName="text-2xl" />
            </div>
          </div>
        </div>

        {/* Date Picker */}
        <div className="flex flex-row items-center justify-center gap-3 flex-wrap">
          {allowFullPayLater ? (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="text-sm gap-0.5 bg-muted/60 border-border-heavy/50 shadow-none hover:bg-muted rounded-full px-4 py-2 flex flex-row items-center justify-center cursor-pointer!"
                >
                  <CalendarIcon className="size-4 inline-block mr-2 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {isToday
                      ? t('paymentTabs.payToday')
                      : selectedDate
                        ? `${t('paymentTabs.payOn')} ${selectedDate.toLocaleDateString()}`
                        : t('paymentTabs.selectDate')}
                  </span>
                  <ChevronDown className="size-4 inline-block ml-2 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto overflow-hidden p-0 rounded-2xl" align="center" side="bottom">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  captionLayout="dropdown-months"
                  startMonth={today}
                  endMonth={maxDate}
                  disabled={(date) => {
                    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    const maxDateOnly = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
                    return dateOnly < todayOnly || dateOnly > maxDateOnly;
                  }}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="text-sm gap-0.5 bg-muted rounded-full px-4 py-2 flex flex-row items-center justify-center cursor-default">
                  <CalendarIcon className="size-4 inline-block mr-2 text-muted-foreground" />
                  <span className="text-sm font-medium">{t('paymentTabs.payToday')}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p>{t('paymentTabs.paymentMustBeToday')}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {needsEmail && onPayerEmailChange && <PayerEmailField id="single-full-payer-email" value={payerEmail} onChange={onPayerEmailChange} />}

        <AffordabilityCommitment />
      </div>

      {/* Multibanco Reference Modal */}
      <Dialog open={!!multibancoRef} onOpenChange={(open) => !open && setMultibancoRef(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('payment.paymentReferenceTitle')}</DialogTitle>
          </DialogHeader>
          {multibancoRef && <MultibancoReference reference={multibancoRef} onClose={() => setMultibancoRef(null)} />}
        </DialogContent>
      </Dialog>

      <BottomActionBar>
        <Button onClick={handleContinueToMethod} size="xl" className="group w-full max-w-sm" disabled={!emailValid}>
          {t('paymentTabs.selectPayment')}
          <ChevronRight className="size-5 ml-2 group-hover:translate-x-1 transition-transform" />
        </Button>
      </BottomActionBar>
    </div>
  );
}
